// Wave RW.1 (2026-05-17) — ERP 웹 PROMISE 출품 폼
// Wave RW.4 (2026-05-17) — 필드 picker 도입: 색상/연료/연식 select, VIN 8자리, 모델 영문 datalist
// 백엔드: POST /api/rfq/inquiries (multipart) + POST /api/rfq/registration-ocr
// autodex-app/app/rfq/new.js (RN) 의 React 포팅. var 사용 + utils/api.authFetch + utils/imageCompress.

import React, { useState, useEffect } from 'react';
import { API, authFetch } from '../../utils/api';
import { compressFiles } from '../../utils/imageCompress';
import { CAR_COLOR_PRESETS } from '../../utils/carColors';
import { CAR_NAME_EN, getEngCarName } from '../../utils/carNames';
import AuctionSelfInspection from '../AuctionSelfInspection';  // Wave R8.2 — 자가검차 평가지 재사용
import MarketPriceCard from '../MarketPriceCard';  // W5 — AUTODEX 시세 (3-tuple: model+year+mileage)

var MIN_PHOTOS = 3;
var MAX_PHOTOS = 12;
var VIN_TAIL_LEN = 8;  // Wave RW.4 — VIN 뒤 8자리 정책 (등록증 OCR 결과 표시 관행)

// 연료 enum (한글, 신규 정의 — 백엔드 자유 텍스트)
var FUEL_OPTIONS = ['휘발유', '경유', 'LPG', '하이브리드', '전기', '수소', '기타'];

// OCR 응답 → 폼 색상 normalize (영/한 모두 처리)
var COLOR_OCR_EN2KO = {
  'WHITE':'흰색', 'BLACK':'검정', 'SILVER':'은색', 'GRAY':'회색', 'GREY':'회색',
  'DARK GRAY':'쥐색', 'DARKGRAY':'쥐색', 'DARK GREY':'쥐색',
  'RED':'빨강', 'BLUE':'파랑', 'NAVY':'남색', 'GREEN':'초록',
  'BEIGE':'베이지', 'YELLOW':'노랑',
};
function normalizeColor(raw) {
  if (!raw) return '';
  var s = String(raw).trim();
  // 1) 정확 label 매칭 (한글 그대로)
  for (var i = 0; i < CAR_COLOR_PRESETS.length; i++) {
    if (CAR_COLOR_PRESETS[i].label === s) return s;
  }
  // 2) 영문 매핑 (대소문자 무관)
  var up = s.toUpperCase();
  if (COLOR_OCR_EN2KO[up]) return COLOR_OCR_EN2KO[up];
  // 3) 매칭 실패 — 빈 값 (사용자 select 유도)
  return '';
}

// OCR 응답 → 폼 연료 normalize
var FUEL_OCR_EN2KO = {
  'GASOLINE':'휘발유', 'PETROL':'휘발유', 'DIESEL':'경유',
  'LPG':'LPG', 'LPGI':'LPG', 'CNG':'LPG',
  'HYBRID':'하이브리드', 'HEV':'하이브리드', 'PHEV':'하이브리드',
  'ELECTRIC':'전기', 'EV':'전기', 'BEV':'전기',
  'HYDROGEN':'수소', 'FCEV':'수소',
};
function normalizeFuel(raw) {
  if (!raw) return '';
  var s = String(raw).trim();
  if (FUEL_OPTIONS.indexOf(s) >= 0) return s;
  var up = s.toUpperCase();
  if (FUEL_OCR_EN2KO[up]) return FUEL_OCR_EN2KO[up];
  return '';
}

// VIN 17자리 → 뒤 8자리 polyfill (OCR 결과 호환)
function normalizeVin(raw) {
  if (!raw) return '';
  var s = String(raw).replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  if (s.length > VIN_TAIL_LEN) return s.slice(-VIN_TAIL_LEN);
  return s;
}

// 모델 영문 자동완성 후보 — CAR_NAME_EN values 중복 제거
var MODEL_EN_LIST = (function() {
  var seen = {};
  var arr = [];
  Object.keys(CAR_NAME_EN).forEach(function(k) {
    var v = CAR_NAME_EN[k];
    if (!seen[v]) { seen[v] = 1; arr.push(v); }
  });
  arr.sort();
  return arr;
})();

// 연식 select 옵션 — 현재 연도 ~ 1990 내림차순
var CURRENT_YEAR = new Date().getFullYear();
var YEAR_OPTIONS = (function() {
  var arr = [];
  for (var y = CURRENT_YEAR; y >= 1990; y--) arr.push(String(y));
  return arr;
})();

function RFQRegister(props) {
  var showToast = props.showToast || function(){};
  var onSubmitted = props.onSubmitted || function(){};

  var [photos, setPhotos] = useState([]);                // File[] (compressed 직후 보관)
  var [previews, setPreviews] = useState([]);            // object-url[] for grid
  var [meta, setMeta] = useState({
    carNo: '', vin: '', year: '', mileage: '', color: '', fuel: '', model: '', memo: '',
    ownerConsented: false,
  });
  // Wave R8.2 (2026-05-20) — 자가검차 평가지 (선택). 객체 {paintData?, damageData?, score?, isAccidentFree?}
  var [selfInspection, setSelfInspection] = useState({});
  var [ocrLoading, setOcrLoading] = useState(false);
  var [submitLoading, setSubmitLoading] = useState(false);
  var [err, setErr] = useState('');
  // feat/rfq-ocr-carno-lock (2026-05-24) — 등록증 OCR 후 carNo/VIN readonly lock
  //   사용자 아이디어: 사진 OCR 결과로 자동 채움된 차량번호/차대번호를 수정 불가 → 의도적 위조 차단
  var [ocrLocked, setOcrLocked] = useState(false);
  // feat/rfq-ocr-mandatory (2026-05-24) — backend signed token (출품 시 동봉)
  var [ocrToken, setOcrToken] = useState('');
  // feat/rfq-quota-indicator (2026-05-24) — 부타무역 신고 #78 대응.
  // mount 시 현재 월 출품 사용량 조회 → indicator 표시 (한도 초과 사후 통보 차단).
  var [quota, setQuota] = useState(null);  // { used, limit, remaining, monthKey } 또는 null (로딩)
  useEffect(function() {
    authFetch(API + '/rfq/quota')
      .then(function(r){ return r.json(); })
      .then(function(d){ if (d && d.success && d.data) setQuota(d.data); })
      .catch(function(){ /* silent — indicator 없어도 POST 시 가드 동작 */ });
  }, []);

  var setField = function(key, val) {
    setMeta(function(prev) {
      var next = Object.assign({}, prev);
      next[key] = val;
      return next;
    });
  };

  // ── 사진 multi-add (이미지 압축 후 보관) ──
  var onPickPhotos = function(e) {
    setErr('');
    var raw = e.target.files;
    if (!raw || !raw.length) return;
    var remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      setErr('사진은 최대 ' + MAX_PHOTOS + '장까지 첨부 가능합니다');
      return;
    }
    var arr = Array.from(raw).slice(0, remaining);
    compressFiles(arr).then(function(comp) {
      var urls = comp.map(function(f) { return URL.createObjectURL(f); });
      setPhotos(function(prev) { return prev.concat(comp); });
      setPreviews(function(prev) { return prev.concat(urls); });
    });
    // input value 비워서 같은 파일 재선택 허용
    try { e.target.value = ''; } catch(_e) {}
  };

  var removePhoto = function(idx) {
    setPhotos(function(prev) { return prev.filter(function(_, i){ return i !== idx; }); });
    setPreviews(function(prev) {
      try { URL.revokeObjectURL(prev[idx]); } catch(_e) {}
      return prev.filter(function(_, i){ return i !== idx; });
    });
  };

  // ── 번호판 사진 OCR (feat/rfq-plate-ocr, 2026-05-24) ──
  //   등록증 없는 경우 대안 — 번호판 사진으로 carNo 만 추출 + lock
  //   TODO: 향후 자동차365 등 외부 API 도입 시 carNo→VIN/모델 자동 채움 (Kyle 결정 대기)
  var onPickPlateOcr = function(e) {
    setErr('');
    var f = e.target.files && e.target.files[0];
    try { e.target.value = ''; } catch(_e) {}
    if (!f) return;
    setOcrLoading(true);
    compressFiles([f]).then(function(arr) {
      var fd = new FormData();
      fd.append('image', arr[0]);
      authFetch(API + '/rfq/plate-ocr', { method: 'POST', body: fd })
        .then(function(r){ return r.json(); })
        .then(function(d) {
          if (d && d.success && d.data && d.data.carNo) {
            var carNo = d.data.carNo;
            setMeta(function(prev) {
              return Object.assign({}, prev, { carNo: carNo });
            });
            // carNo 만 lock (VIN 은 번호판에 없음 — 수기 허용 또는 향후 API 보강)
            setOcrLocked(true);
            // feat/rfq-ocr-mandatory (2026-05-24) — backend signed token 저장
            if (d.data.ocrToken) setOcrToken(d.data.ocrToken);
            showToast('번호판 인식 완료 — 차량번호 잠금 ✓', 'success');
          } else {
            setErr((d && d.error) || '번호판 인식 실패 — 등록증 OCR 또는 수동 입력');
          }
        })
        .catch(function(){ setErr('네트워크 오류 — 번호판 OCR'); })
        .finally(function(){ setOcrLoading(false); });
    });
  };

  // ── 등록증 OCR ──
  var onPickRegOcr = function(e) {
    setErr('');
    var f = e.target.files && e.target.files[0];
    try { e.target.value = ''; } catch(_e) {}
    if (!f) return;
    setOcrLoading(true);
    // OCR 도 5MB 한계 — 사전 압축
    compressFiles([f]).then(function(arr) {
      var fd = new FormData();
      fd.append('image', arr[0]);
      authFetch(API + '/rfq/registration-ocr', { method: 'POST', body: fd })
        .then(function(r){ return r.json(); })
        .then(function(d) {
          if (d && d.success && d.data) {
            var info = d.data;
            setMeta(function(prev) {
              // Wave RW.4 — OCR 결과 normalize (영문/한글 둘 다 처리)
              var nColor = normalizeColor(info.color);
              var nFuel  = normalizeFuel(info.fuel);
              var nVin   = info.vin ? normalizeVin(info.vin) : prev.vin;
              var nModel = info.model ? getEngCarName(info.model) : prev.model;  // 한글 → 영문 매핑, 영문이면 그대로
              return Object.assign({}, prev, {
                carNo:   info.carNo  || prev.carNo,
                vin:     nVin,
                year:    info.year != null ? String(info.year) : prev.year,
                mileage: info.mileage != null ? String(info.mileage) : prev.mileage,
                color:   nColor || prev.color,
                fuel:    nFuel  || prev.fuel,
                model:   nModel,
              });
            });
            // feat/rfq-ocr-carno-lock (2026-05-24) — OCR 인증 후 carNo/VIN 잠금 (위조 차단)
            //   carNo 또는 vin 둘 중 하나라도 추출되면 lock (부분 lock 도 사용자가 다른 거 못 바꾸게)
            if (info.carNo || info.vin) {
              setOcrLocked(true);
            }
            // feat/rfq-ocr-mandatory (2026-05-24) — backend signed token 저장
            if (info.ocrToken) setOcrToken(info.ocrToken);
            showToast('등록증 OCR 완료 — 차량번호/차대번호 잠금 ✓', 'success');
          } else {
            setErr((d && d.error) || 'OCR 실패 — 수동 입력해주세요');
          }
        })
        .catch(function(){ setErr('네트워크 오류 — 등록증 OCR'); })
        .finally(function(){ setOcrLoading(false); });
    });
  };

  // ── 출품 ──
  var validate = function() {
    if (photos.length < MIN_PHOTOS) {
      setErr('사진 최소 ' + MIN_PHOTOS + '장 첨부해주세요 (현재 ' + photos.length + '장)');
      return false;
    }
    if (!meta.carNo && !meta.vin) {
      setErr('차량번호 또는 VIN 중 하나는 입력해주세요');
      return false;
    }
    return true;
  };

  var onSubmit = function() {
    setErr('');
    if (!validate()) return;
    setSubmitLoading(true);

    var fd = new FormData();
    photos.forEach(function(f) { fd.append('photos', f); });
    ['carNo','vin','year','mileage','color','fuel','model','memo'].forEach(function(k) {
      if (meta[k] != null && meta[k] !== '') fd.append(k, String(meta[k]));
    });
    if (meta.ownerConsented) fd.append('ownerConsented', 'true');
    // feat/rfq-ocr-mandatory (2026-05-24) — backend signed token 동봉 (OCR 의무화 검증)
    if (ocrToken) fd.append('ocrToken', ocrToken);
    // Wave R8.2 (2026-05-20) — selfInspection 비어있지 않으면 JSON 으로 첨부
    var siHasData = selfInspection && (
      (selfInspection.paintData && Object.keys(selfInspection.paintData).length > 0) ||
      (selfInspection.damageData && Object.keys(selfInspection.damageData).length > 0) ||
      (selfInspection.score === 0 || selfInspection.score > 0) ||
      selfInspection.isAccidentFree === true
    );
    if (siHasData) {
      fd.append('selfInspection', JSON.stringify(selfInspection));
    }

    authFetch(API + '/rfq/inquiries', { method: 'POST', body: fd })
      .then(function(r){ return r.json(); })
      .then(function(d) {
        if (d && d.success && d.data) {
          showToast('PROMISE 출품 완료 — 48시간 동안 바이어 시세가 모입니다', 'success');
          onSubmitted(d.data.id);
        } else {
          var code = d && d.errorCode;
          if (code === 'RFQ_QUOTA_EXCEEDED') {
            setErr('월 출품 한도를 초과했습니다. 다음 달 1일에 갱신됩니다');
          } else if (code === 'SELLER_MANNER_TOO_LOW') {
            setErr('매너온도가 낮아 출품할 수 없습니다 (현재 ' + (d.currentManner != null ? Number(d.currentManner).toFixed(1) : '?') + '°, 최소 25°)');
          } else if (code === 'DUPLICATE_PHOTOS') {
            setErr('동일한 사진이 중복 첨부됐습니다. 서로 다른 사진을 첨부해주세요');
          } else {
            setErr((d && d.error) || '출품 실패');
          }
        }
      })
      .catch(function(){ setErr('네트워크 오류 — 출품 요청'); })
      .finally(function(){ setSubmitLoading(false); });
  };

  // ── UI ──
  // feat/rfq-quota-indicator (2026-05-24) — quota.remaining=0 시 submit 자체 차단 (사진 업로드 후 헛수고 방지)
  var quotaBlocked = !!(quota && quota.remaining === 0);
  // feat/rfq-ocr-mandatory (2026-05-24) — OCR 의무화 정책: ocrLocked + ocrToken 모두 있어야 출품 가능
  var canSubmit = photos.length >= MIN_PHOTOS && (meta.carNo || meta.vin) && !submitLoading && !quotaBlocked && ocrLocked && !!ocrToken;

  return (
    <div style={{ padding: '20px 24px', maxWidth: 880, margin: '0 auto' }}>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 16 }}>
        🤝 PROMISE 출품
      </h2>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12, lineHeight: 1.6 }}>
        차량 사진과 정보를 등록하면 바이어들이 시세를 제안합니다 (48시간 유효).
        등록증 사진 1장만 있어도 차량 정보가 자동 채워집니다.
      </div>

      {/* feat/rfq-quota-indicator (2026-05-24) — 이번 달 출품 사용량 indicator */}
      {quota && (function(){
        var pct = quota.limit > 0 ? Math.round((quota.used / quota.limit) * 100) : 0;
        var danger = quota.remaining === 0;
        var warn = !danger && quota.remaining <= 1;
        var bg = danger ? '#fef2f2' : (warn ? '#fefce8' : '#f0fdf4');
        var border = danger ? '#fecaca' : (warn ? '#fde68a' : '#bbf7d0');
        var fg = danger ? '#991b1b' : (warn ? '#854d0e' : '#166534');
        var barColor = danger ? '#dc2626' : (warn ? '#f59e0b' : '#16a34a');
        return (
          <div style={{
            padding: '10px 14px', marginBottom: 16,
            background: bg, border: '1px solid ' + border, borderRadius: 10,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, fontSize: 13, fontWeight: 700, color: fg }}>
              <span>📊 이번 달 출품 한도 ({quota.monthKey})</span>
              <span dir="ltr">{quota.used} / {quota.limit} · 잔여 <strong>{quota.remaining}</strong>건</span>
            </div>
            <div style={{ height: 6, background: 'rgba(0,0,0,0.06)', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{ width: pct + '%', height: '100%', background: barColor, transition: 'width 0.3s' }} />
            </div>
            {danger && (
              <div style={{ marginTop: 6, fontSize: 12, color: fg }}>
                이번 달 출품 한도를 모두 사용했습니다. 다음 달 1일에 갱신됩니다.
              </div>
            )}
          </div>
        );
      })()}

      {/* 등록증 OCR */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <label style={btnPrimary(ocrLoading)}>
            {ocrLoading ? '📷 분석 중...' : '📷 등록증으로 자동 채움'}
            <input type="file" accept="image/*" capture="environment" onChange={onPickRegOcr}
              disabled={ocrLoading} style={{ display: 'none' }} />
          </label>
          {/* feat/rfq-plate-ocr (2026-05-24) — 등록증 없는 경우 대안 (번호판 사진만으로 carNo lock) */}
          <label style={btnSecondary(ocrLoading)}>
            {ocrLoading ? '📷 분석 중...' : '🔢 번호판 사진으로 차량번호'}
            <input type="file" accept="image/*" capture="environment" onChange={onPickPlateOcr}
              disabled={ocrLoading} style={{ display: 'none' }} />
          </label>
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
          등록증이 없으면 번호판 사진만으로도 차량번호 잠금 가능 (위조 차단)
        </div>
      </div>

      {/* 차량 정보 — Wave RW.4: 색상/연료/연식 select + VIN 8자리 + 모델 영문 datalist */}
      <div style={sectionCard}>
        <div style={sectionTitle}>차량 정보</div>
        <div style={row2}>
          <Field label="차량번호" value={meta.carNo} onChange={function(v){ setField('carNo', v); }} placeholder="12가3456"
            locked={ocrLocked} />
          <Field label={'VIN (뒤 ' + VIN_TAIL_LEN + '자리)'} value={meta.vin}
            onChange={function(v){ setField('vin', normalizeVin(v)); }}
            placeholder="7A049586"
            maxLength={VIN_TAIL_LEN}
            locked={ocrLocked} />
        </div>
        {/* feat/rfq-ocr-mandatory (2026-05-24) — 수동 잠금 해제 버튼 제거 (사용자 요청). OCR 오인식 시 사진 재촬영 유도. */}
        {ocrLocked && (
          <div style={{ marginTop: -4, marginBottom: 10, fontSize: 11, color: '#64748b' }}>
            🔒 OCR 인증된 차량번호 — 수정 불가 (위조 차단). 오인식 시 다른 사진으로 재OCR.
          </div>
        )}
        {!ocrLocked && (
          <div style={{ marginTop: -4, marginBottom: 10, fontSize: 12, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px' }}>
            ⚠️ <strong>OCR 인증 필수</strong> — 등록증 또는 번호판 사진으로 차량번호 인식 후 출품 가능합니다.
          </div>
        )}
        <div style={row2}>
          <SelectField label="연식" value={meta.year} onChange={function(v){ setField('year', v); }}
            options={YEAR_OPTIONS} placeholder="-- 선택 --" />
          <Field label="주행거리 (km)" value={meta.mileage} onChange={function(v){ setField('mileage', v); }} placeholder="50000" type="number" />
        </div>
        <div style={row2}>
          <ColorSelectField label="색상" value={meta.color} onChange={function(v){ setField('color', v); }} />
          <SelectField label="연료" value={meta.fuel} onChange={function(v){ setField('fuel', v); }}
            options={FUEL_OPTIONS} placeholder="-- 선택 --" />
        </div>
        <Field label="모델 (영문)" value={meta.model}
          onChange={function(v){ setField('model', v.toUpperCase()); }}
          placeholder="GRANDEUR, SONATA, K5..."
          list="rfq-model-list" />
        <datalist id="rfq-model-list">
          {MODEL_EN_LIST.map(function(m){ return <option key={m} value={m} />; })}
        </datalist>
        <MarketPriceCard model={meta.model} year={meta.year} mileage={meta.mileage} locale="ko" />
        <Field label="메모 (선택)" value={meta.memo} onChange={function(v){ setField('memo', v); }} placeholder="추가 정보..." multiline />
      </div>

      {/* 사진 */}
      <div style={sectionCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <div style={sectionTitle}>사진 ({photos.length}/{MAX_PHOTOS})</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: photos.length < MIN_PHOTOS ? '#dc2626' : '#16a34a' }}>
            {photos.length < MIN_PHOTOS ? ('최소 ' + MIN_PHOTOS + '장 필수') : '✓ 충족'}
          </div>
        </div>
        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10 }}>
          외관 사진 위주로 자유롭게 첨부하세요. 내관/계기판/엔진 등은 선택사항입니다.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10 }}>
          {previews.map(function(url, idx) {
            return (
              <div key={url + '_' + idx} style={photoCell}>
                <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                <button type="button" onClick={function(){ removePhoto(idx); }} style={removeBtn}
                  title="제거">×</button>
                <div style={photoIdx}>{idx + 1}</div>
              </div>
            );
          })}
          {photos.length < MAX_PHOTOS && (
            <label style={addCell}>
              <span style={{ fontSize: 28, color: '#1B3A5C' }}>+</span>
              <span style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>추가</span>
              <input type="file" accept="image/*" multiple onChange={onPickPhotos} style={{ display: 'none' }} />
            </label>
          )}
        </div>
      </div>

      {/* Wave R8.2 (2026-05-20) — 자가검차 평가지 (선택, 입력 권장) */}
      <div style={sectionCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <div style={sectionTitle}>🔍 차량 상태 (선택)</div>
          <div style={{ fontSize: 11, color: '#0369a1', fontWeight: 600 }}>입력하면 견적 정확도 ↑</div>
        </div>
        <div style={{ fontSize: 12, color: '#475569', marginBottom: 10, lineHeight: 1.5 }}>
          도막/사고 부위/종합 점수를 솔직하게 입력하세요. <strong>정확한 차량 정보가 정확한 견적을 부릅니다.</strong>
        </div>
        {/* 허위 정보 경고 */}
        <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12, lineHeight: 1.5, color: '#92400e' }}>
          ⚠️ <strong>허위 정보 주의</strong> — 실제와 다른 도막/사고 정보 입력 시:<br/>
          • 약속 위반 신고 대상 (매너온도 -10° / -15° 차감)<br/>
          • 매너온도 25° 미만 시 PROMISE 출품 차단
        </div>
        <AuctionSelfInspection value={selfInspection} onChange={setSelfInspection} hideEngineVideo={true} />
      </div>

      {/* 차주 동의 */}
      <label style={consentRow}>
        <input type="checkbox" checked={meta.ownerConsented}
          onChange={function(e){ setField('ownerConsented', e.target.checked); }}
          style={{ marginRight: 10, width: 18, height: 18 }} />
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>차주에게 매도 의사 사전 확인 완료</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
            체크 시 입찰자에게 "차주 동의" 뱃지 표시. 입찰 신뢰도 ↑
          </div>
        </div>
      </label>

      {/* 에러 */}
      {err && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
          {err}
        </div>
      )}

      {/* 제출 */}
      <button type="button" onClick={onSubmit} disabled={!canSubmit} style={submitBtn(!canSubmit)}>
        {submitLoading ? '출품 중...' : '출품하기'}
      </button>
    </div>
  );
}

function Field(props) {
  var label = props.label, value = props.value, onChange = props.onChange, placeholder = props.placeholder;
  var type = props.type || 'text';
  var multiline = props.multiline;
  var maxLength = props.maxLength;
  var list = props.list;  // Wave RW.4 — datalist autocomplete (모델 영문)
  // feat/rfq-ocr-carno-lock (2026-05-24) — readOnly + lock 안내
  var locked = !!props.locked;
  var lockedStyle = locked ? { background: '#f1f5f9', color: '#475569', cursor: 'not-allowed' } : {};
  var lockedInputStyle = Object.assign({}, inputBase, lockedStyle);
  return (
    <div style={{ flex: 1, marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>{label}</span>
        {locked && (
          <span title="OCR 인증된 값 — 수정 불가" style={{ fontSize: 10, fontWeight: 700, color: '#16a34a', background: '#dcfce7', padding: '1px 6px', borderRadius: 999 }}>
            🔒 OCR 인증
          </span>
        )}
      </div>
      {multiline ? (
        <textarea value={value || ''} onChange={function(e){ if (!locked) onChange(e.target.value); }} placeholder={placeholder}
          rows={2}
          readOnly={locked}
          style={lockedInputStyle} />
      ) : (
        <input type={type} value={value || ''} onChange={function(e){ if (!locked) onChange(e.target.value); }} placeholder={placeholder}
          maxLength={maxLength}
          list={list}
          readOnly={locked}
          style={lockedInputStyle} />
      )}
    </div>
  );
}

// Wave RW.4 — 일반 select (연식/연료)
function SelectField(props) {
  var label = props.label, value = props.value, onChange = props.onChange;
  var options = props.options || [];
  var placeholder = props.placeholder || '-- 선택 --';
  return (
    <div style={{ flex: 1, marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{label}</div>
      <select value={value || ''} onChange={function(e){ onChange(e.target.value); }}
        style={inputBase}>
        <option value="">{placeholder}</option>
        {options.map(function(o){ return <option key={o} value={o}>{o}</option>; })}
      </select>
    </div>
  );
}

// Wave RW.4 — 색상 select + 색점 프리뷰 (CarRegisterForm 패턴 재사용)
function ColorSelectField(props) {
  var label = props.label, value = props.value, onChange = props.onChange;
  var preset = value ? CAR_COLOR_PRESETS.find(function(p){ return p.label === value; }) : null;
  return (
    <div style={{ flex: 1, marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {preset && (
          <div style={{
            width: 22, height: 22, borderRadius: '50%',
            background: preset.hex,
            border: '1px solid ' + (preset.border || '#e2e8f0'),
            flexShrink: 0,
          }} />
        )}
        <select value={value || ''} onChange={function(e){ onChange(e.target.value); }}
          style={Object.assign({}, inputBase, { flex: 1 })}>
          <option value="">-- 선택 --</option>
          {CAR_COLOR_PRESETS.map(function(p){ return <option key={p.key} value={p.label}>{p.label}</option>; })}
        </select>
      </div>
    </div>
  );
}

// ── styles ──
var inputBase = {
  width: '100%', boxSizing: 'border-box',
  padding: '8px 12px', fontSize: 14,
  border: '1.5px solid #e2e8f0', borderRadius: 8,
  background: 'white', color: '#0f172a',
  fontFamily: "'Pretendard',sans-serif",
};
var sectionCard = {
  background: 'white', borderRadius: 12,
  border: '1px solid #e2e8f0', padding: 16,
  marginBottom: 16,
};
var sectionTitle = {
  fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 12,
};
var row2 = { display: 'flex', gap: 12 };
var photoCell = {
  position: 'relative', aspectRatio: '4/3',
  borderRadius: 8, overflow: 'hidden',
  border: '1px solid #e2e8f0', background: '#f1f5f9',
};
var addCell = {
  display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center',
  aspectRatio: '4/3',
  border: '2px dashed #cbd5e1', borderRadius: 8,
  cursor: 'pointer', background: '#f8fafc',
};
var removeBtn = {
  position: 'absolute', top: 4, right: 4,
  width: 24, height: 24,
  border: 'none', borderRadius: '50%',
  background: 'rgba(15,23,42,0.7)', color: 'white',
  fontSize: 16, fontWeight: 700, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  lineHeight: 1,
};
var photoIdx = {
  position: 'absolute', bottom: 4, left: 4,
  background: 'rgba(15,23,42,0.7)', color: 'white',
  padding: '2px 6px', borderRadius: 4,
  fontSize: 10, fontWeight: 700,
};
var consentRow = {
  display: 'flex', alignItems: 'flex-start',
  padding: '14px 16px',
  background: 'white', borderRadius: 12,
  border: '1px solid #e2e8f0',
  cursor: 'pointer', marginBottom: 16,
};
function btnPrimary(disabled) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '10px 18px',
    background: disabled ? '#94a3b8' : '#1B3A5C',
    color: 'white', borderRadius: 8,
    fontSize: 14, fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
    border: 'none',
    fontFamily: "'Pretendard',sans-serif",
  };
}

// feat/rfq-plate-ocr (2026-05-24) — 번호판 OCR 보조 버튼 스타일 (outline, primary 와 시각 구분)
function btnSecondary(disabled) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '10px 18px',
    background: 'white',
    color: disabled ? '#94a3b8' : '#1B3A5C',
    borderRadius: 8,
    fontSize: 14, fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
    border: '1.5px solid ' + (disabled ? '#cbd5e1' : '#1B3A5C'),
    fontFamily: "'Pretendard',sans-serif",
  };
}
function submitBtn(disabled) {
  return {
    width: '100%', padding: '14px 0',
    background: disabled ? '#cbd5e1' : '#1B3A5C',
    color: 'white', borderRadius: 10,
    fontSize: 16, fontWeight: 800,
    cursor: disabled ? 'not-allowed' : 'pointer',
    border: 'none',
    fontFamily: "'Pretendard',sans-serif",
  };
}

export default RFQRegister;
